<template>
  <div :class="$style.container">
    <UserDevicesWidget
      :devices="devices"
      @navigateToFullList="navigateToAsideTab(navigation.devices)"
      @navigationMethod="navigateToAsideTab(navigation.devices)"
    />
    <div :class="$style.grid">
      <UserGroupsWidget
        :groups="groups"
        @navigateToFullList="navigateToAsideTab(navigation.userGroups)"
        @navigationMethod="navigateToAsideTab(navigation.userGroups)"
      />
      <UserApplicationsWidget
        :applications="applications"
        :ssoRoute="ssoRoute"
        @navigateToFullList="openApplicationModal"
        @navigationMethod="navigateToSSOAside"
      />
    </div>
    <UserHighlightsEventSummary />
  </div>
</template>

<script>
import * as Constants from 'client/util/Constants';
import ApplicationApi from 'client/api/v2/ApplicationApi';
import ModalEventBus from 'components/Modal/ModalEventBus';
import SystemApi from 'client/api/v2/SystemApi';
import SystemUserApi from 'client/api/v2/SystemUserApi';
import UserApplicationsWidget from 'client/systemusers/UserHighlights/components/UserApplicationsWidget.vue';
import UserDevicesWidget from 'client/systemusers/UserHighlights/components/UserDevicesWidget.vue';
import UserGroupApi from 'client/api/v2/UserGroupApi';
import UserGroupsWidget from 'client/systemusers/UserHighlights/components/UserGroupsWidget.vue';
import UserHighlightsApplicationsModal from 'client/systemusers/UserHighlights/UserHighlightsApplicationsModal.vue';
import UserHighlightsEventSummary from 'client/systemusers/UserHighlights/UserHighlightsEventSummary.vue';

const { ItemTypeConsts, ItemTypeRoutes, DeviceConsts } = Constants;

export default {
  name: 'UserHighlights',

  components: {
    UserApplicationsWidget,
    UserDevicesWidget,
    UserGroupsWidget,
    UserHighlightsEventSummary,
  },

  props: {
    itemId: {
      type: String,
      default: '',
    },
    navigateToAsideTab: {
      type: Function,
      default: () => {},
    },
  },

  data: () => ({
    applications: {
      hasError: false,
      isLoading: true,
      resources: [],
      showLinkToFullList: true,
      totalCount: undefined,
    },
    devices: {
      hasError: false,
      isLoading: true,
      resources: [],
      showLinkToFullList: true,
      totalCount: undefined,
    },
    groups: {
      hasError: false,
      isLoading: true,
      resources: [],
      showLinkToFullList: true,
      totalCount: undefined,
    },
    navigation: DeviceConsts.navigation,
    ssoRoute: ItemTypeRoutes[ItemTypeConsts.sso],
  }),

  created() {
    this.fetchBoundApplications();
    this.fetchBoundDevices();
    this.fetchBoundGroups();
  },

  methods: {
    async fetchBoundApplications() {
      try {
        const { results, totalCount } = await SystemUserApi.getBoundApplications(this.itemId, {
          limit: 5,
        });
        this.applications.totalCount = totalCount;
        if (totalCount) {
          const applicationIds = results.map(d => d.id);
          const data = await Promise.all(applicationIds.map(id => ApplicationApi.get(id)));
          this.applications.resources = data.map(d => ({
            name: d.displayLabel,
            href: `${ItemTypeRoutes[ItemTypeConsts.application]}/${d.id}/details`,
          }));
        }
      } catch {
        this.applications.hasError = true;
      } finally {
        this.applications.isLoading = false;
      }
    },

    async fetchBoundDevices() {
      try {
        const { results, totalCount } = await SystemUserApi.getBoundDevices(this.itemId, {
          limit: 3,
        });
        this.devices.totalCount = totalCount;

        if (totalCount) {
          const response = await SystemApi.search({
            fields: ['displayName'],
            filter: { or: results.map(d => ({ _id: d.id })) },
            limit: 3,
          });

          this.devices.resources = response.data.results.map(d => ({
            name: d.displayName,
            href: `${ItemTypeRoutes[ItemTypeConsts.device]}/${d._id}/details`,
          }));
        }
      } catch {
        this.devices.hasError = true;
      } finally {
        this.devices.isLoading = false;
      }
    },

    async fetchBoundGroups() {
      try {
        const { results, totalCount } = await SystemUserApi.getBoundUserGroups(this.itemId, {
          limit: 5,
        });
        this.groups.totalCount = totalCount;

        if (totalCount) {
          const groupIds = results.map(d => d.id);
          const data = await Promise.all(groupIds.map(id => UserGroupApi.get(id)));

          this.groups.resources = data.map(d => ({
            name: d.name,
            href: `${ItemTypeRoutes[ItemTypeConsts.userGroup]}/${d.id}/details`,
          }));
        }
      } catch {
        this.groups.hasError = true;
      } finally {
        this.groups.isLoading = false;
      }
    },

    navigateToSSOAside() {
      this.$router.push('/sso/choose');
    },

    openApplicationModal() {
      ModalEventBus.open({
        component: UserHighlightsApplicationsModal,
        componentProps: {
          userId: this.itemId,
          totalCount: this.applications.totalCount,
        },
        isLargeModal: true,
      });
    },
  },
};
</script>

<style module>
@import '../../components/Highlights/css/highlights.css';
</style>
